import React from "react"

import { Truck } from "lucide-react"

import { type VehicleCapacityInfo } from "@/lib/routeCostCalculation"
import { loadFactorTone } from "@/lib/tone"
import { cn } from "@/lib/utils"
import { Stop } from "@/types/collections"

interface LoadFactorCardProps {
  capacity: VehicleCapacityInfo | null
  stops: Stop[]
  vehicleCapacity?: number | null
}

const LoadFactorCard: React.FC<LoadFactorCardProps> = ({ capacity, stops, vehicleCapacity }) => {
  if (!capacity) {
    const currentWeight = stops.reduce((total, stop) => {
      return total + (stop.weightActual || stop.weightEstimated || 0)
    }, 0)
    return (
      <div className="rounded-lg border border-palette-jungleGreen200 bg-palette-jungleGreen000 p-3">
        <div className="mb-1 flex items-center gap-2 text-xs text-palette-jungleGreen500">
          <Truck size={12} />
          Load Factor
        </div>
        <div className="font-medium text-palette-jungleGreen900">
          {vehicleCapacity
            ? `${Math.round(currentWeight)} / ${vehicleCapacity} kg`
            : currentWeight > 0
              ? `${Math.round(currentWeight)} kg`
              : "Vehicle type not configured"}
        </div>
      </div>
    )
  }

  const tone = loadFactorTone(capacity.totalFillPercentage)

  const contributions = Object.entries(capacity.usedCapacity)
    .filter(([_, used]) => used > 0)
    .map(([unitType, used]) => {
      const unitTypeKey = unitType as keyof typeof capacity.vehicleCapacity
      const unitCapacity = capacity.vehicleCapacity[unitTypeKey]
      const fillContribution = unitCapacity > 0 ? (used / unitCapacity) * 100 : 0

      const unitTypeName = unitType.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())

      return { unitTypeName, used, fillContribution }
    })

  const basedOnActuals = stops.some(
    (s) =>
      s.loadingUnitsActual !== undefined &&
      s.loadingUnitsActual !== null &&
      s.loadingUnitsActual > 0
  )

  return (
    <div className="rounded-xl border border-purple-200/50 bg-gradient-to-br from-purple-50 to-indigo-50 p-4 shadow-sm">
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="rounded-lg bg-purple-100 p-1.5">
            <Truck size={16} className="text-purple-700" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-palette-jungleGreen900">Load Factor</h3>
            <p className="text-xs text-palette-jungleGreen500">Fill percentage</p>
          </div>
        </div>
        <div className="text-right">
          <div
            className={cn(
              "text-2xl font-bold",
              tone === "danger"
                ? "text-red-600"
                : tone === "warning"
                  ? "text-amber-600"
                  : "text-green-600"
            )}
          >
            {capacity.totalFillPercentage.toFixed(0)}%
          </div>
          <div className="mt-0.5 text-xs text-palette-jungleGreen500">Vehicle Fill</div>
        </div>
      </div>

      <div className="mb-4 h-3 w-full rounded-full bg-palette-jungleGreen200">
        <div
          className={cn(
            "h-3 rounded-full transition-all",
            tone === "danger" ? "bg-red-500" : tone === "warning" ? "bg-amber-500" : "bg-green-500"
          )}
          style={{ width: `${Math.min(capacity.totalFillPercentage, 100)}%` }}
        />
      </div>

      {contributions.length === 0 ? (
        <div className="py-2 text-center text-sm text-palette-jungleGreen500">
          No loading units on board
        </div>
      ) : (
        <div className="space-y-2">
          <div className="mb-2 text-xs font-medium text-palette-jungleGreen600">
            What's on board:
          </div>
          {contributions.map(({ unitTypeName, used, fillContribution }) => (
            <div key={unitTypeName} className="flex items-center justify-between text-sm">
              <span className="text-palette-jungleGreen700">
                {used} {unitTypeName}
              </span>
              <span
                className={cn(
                  "font-medium",
                  fillContribution >= 50
                    ? "text-amber-600"
                    : fillContribution >= 25
                      ? "text-blue-600"
                      : "text-palette-jungleGreen600"
                )}
              >
                {fillContribution.toFixed(0)}%
              </span>
            </div>
          ))}
        </div>
      )}

      <div className="mt-3 border-t border-purple-100 pt-2 text-[10px] text-palette-jungleGreen400">
        {basedOnActuals ? "Based on actuals" : "Based on forecast"}
      </div>
    </div>
  )
}

export default LoadFactorCard
